import { useState, useEffect, useCallback, useRef } from 'react';
import dgEngineService from '../services/dgEngineService';

// 폴링 간격: 10초
const POLL_INTERVAL_MS = 10000;

/**
 * DG Engine 사용자 프로파일 훅
 * 성향 프로파일과 상태 벡터를 가져옴
 * - 마운트 시 즉시 로드
 * - 이후 30초마다 자동 갱신 (페이지 새로고침 불필요)
 */
export function useDGProfile(userId) {
  const [profile, setProfile] = useState(null);
  const [stateVector, setStateVector] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const timerRef = useRef(null);

  const fetchProfile = useCallback(async () => {
    if (!userId) return;
    // 최초 로드가 아닌 폴링 갱신은 로딩 표시 생략 (깜빡임 방지)
    setError(null);
    try {
      const [profileData, stateData] = await Promise.all([
        dgEngineService.getProfile(userId),
        dgEngineService.getState(userId),
      ]);
      if (profileData && !profileData.error) setProfile(profileData);
      // getState()는 { stateVector: {...}, lastFastUpdate, dataPoints } 반환
      // 실제 벡터값만 꺼내서 저장 (이중 중첩 방지)
      if (stateData && !stateData.error) {
        setStateVector(stateData.stateVector ?? stateData);
      }
    } catch (err) {
      setError(err.message);
    }
  }, [userId]);

  // 최초 로드 (로딩 표시 있음)
  const fetchProfileWithLoading = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    await fetchProfile();
    setLoading(false);
  }, [userId, fetchProfile]);

  useEffect(() => {
    fetchProfileWithLoading();

    // 30초마다 자동 갱신
    timerRef.current = setInterval(() => {
      fetchProfile();
    }, POLL_INTERVAL_MS);

    // 수락/거절/제안 직후 즉시 갱신 + 2초 후 재갱신 (서버 DB 반영 완료 대기)
    const onSuggestionUpdate = () => {
      fetchProfile();
      setTimeout(fetchProfile, 2000);
    };
    window.addEventListener('suggestionUpdate', onSuggestionUpdate);

    // DG Engine 소켓 알림 → 즉시 + 2초 후 재갱신
    // (logFromAresResult가 fire-and-forget이라 DG Engine 처리 완료까지 시간 필요)
    const onDGProfileUpdate = () => {
      fetchProfile();
      setTimeout(fetchProfile, 2000);
    };
    window.addEventListener('dgProfileUpdate', onDGProfileUpdate);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      window.removeEventListener('suggestionUpdate', onSuggestionUpdate);
      window.removeEventListener('dgProfileUpdate', onDGProfileUpdate);
    };
  }, [fetchProfileWithLoading, fetchProfile]);

  return { profile, stateVector, loading, error, refetch: fetchProfile };
}

export default useDGProfile;
